/**
 * Pages Container Module — lists all report and html_viewer modules.
 * Similar to Drive listing folders, but for pages.
 */
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/keys";
import { api } from "@/lib/api";
import { cancelBuild } from "@/lib/api/endpoints/chat";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import type { Module } from "@/types/modules";
import { ReportModule } from "@/modules/pages/report/ReportModule";
import { synthesizeReportHtml } from "@/modules/pages/report/synthesizeReportHtml";
import HtmlViewerModule from "@/modules/pages/artifacts/HtmlViewerModule";
import PageRenderer from "@/modules/pages/PageRenderer";
import HistoryDrawer from "@/modules/pages/HistoryDrawer";
import PageRowThumbnail from './PageRowThumbnail';
import { readPageObjAttr, parseEntryId, formatDate, findEntryByPageIdFamily } from './utils/page-entries';
import { toCanonicalPageId } from '@/lib/page-id';
import type { PageEntry } from './types';
import { env } from "@/lib/env";
import { ModuleSearchBar } from "@/common/components/ModuleSearchBar";
import { MobileSearchToggle } from "@/common/components/MobileSearchToggle";
import { MobileActionButton } from "@/common/components/MobileActionButton";
import { ModuleEmptyState } from "../_infrastructure/ModuleEmptyState";
import './PagesModule.css';

interface PagesContainerProps {
  module: Module;
  conversationId: string;
}

export default function PagesContainerModule({
  module,
  conversationId,
}: PagesContainerProps) {
  const { token, selectedContainer } = useSession();
  const queryClient = useQueryClient();
  const workspaceId =
    selectedContainer?.id ??
    selectedContainer?.ownerId ??
    getActiveWorkspaceId();

  // Store only the currently-selected page id. The live module object is
  // derived from the `pages` array on every render below, so React Query
  // refetches (e.g. after a build persists new config.html) flow through
  // to the preview without any manual wiring. A previous implementation
  // snapshotted the module object at click time, which froze stale config
  // forever — existing pages would render blank because the cached
  // `config.html` at click time was empty.
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  // Build ids whose row-query invalidation already fired from the live-event
  // fallback below — gates that invalidation to once per build (the events
  // re-fire per streamed chunk; see the comment at the call site).
  const invalidatedBuildIdsRef = useRef<Set<string>>(new Set());
  // One-shot cleanup: drop legacy xavior:pages:locallyHidden:* keys left
  // over from the symptom-patch that PR #1853 made obsolete. Without this
  // sweep, browsers that hid rows pre-fix would carry stale entries
  // forever (the in-memory state is gone but the localStorage value
  // remains). Runs once on mount per workspace; cheap enough to do
  // unconditionally — Object.keys on localStorage is bounded by user
  // history, not workspace count.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const prefix = 'xavior:pages:locallyHidden:';
      const toRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith(prefix)) toRemove.push(key);
      }
      for (const key of toRemove) window.localStorage.removeItem(key);
    } catch {
      // Storage unavailable — nothing to clean up.
    }
  }, []);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'private' | 'public'>('all');
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState("");
  // The "New" button used to always dispatch "Create a report about: ..."
  // — a webpage-style request (landing page, portfolio, brand-mimic) had
  // no entry point through this modal. Two-up picker now lets the user
  // pick which kind of artifact they want and shapes the dispatched
  // chat prompt accordingly.
  const [newPageKind, setNewPageKind] = useState<'report' | 'webpage'>('report');
  // buildMethod state removed — always use chat (background infra exists for scheduled reports)
  const [contextMenu, setContextMenu] = useState<{
    id: string;
    name: string;
    x: number;
    y: number;
  } | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameText, setRenameText] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  // Version-history drawer state. Opened from the preview-view header's
  // History button when viewing an object-sourced page. Gated on
  // source === 'object' because snapshots live on the `page` object id;
  // the legacy html_viewer-module path predates the snapshot system.
  const [historyOpen, setHistoryOpen] = useState(false);
  // Mirror of PageRenderer's inspect/edit mode so the Edit button can show
  // an `is-active` blue state. PageRenderer broadcasts state changes via
  // `xavior:inspect-state-changed`; we never set this directly except in
  // response to that event.
  const [editActive, setEditActive] = useState(false);
  useEffect(() => {
    const onState = (e: Event) => {
      const detail = (e as CustomEvent).detail as { active?: boolean } | undefined;
      setEditActive(Boolean(detail?.active));
    };
    window.addEventListener('xavior:inspect-state-changed', onState as EventListener);
    return () => window.removeEventListener('xavior:inspect-state-changed', onState as EventListener);
  }, []);

  // Listen for "View Archived" from ⋯ menu
  useEffect(() => {
    const handleToggle = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.moduleId === module.id) setShowArchived(prev => !prev);
    };
    window.addEventListener('xavior:toggle-archived-tasks', handleToggle);
    return () => window.removeEventListener('xavior:toggle-archived-tasks', handleToggle);
  }, [module.id]);

  // Listen for "open this child" requests fired by the chat layer when a
  // report or page build finishes. The chat focuses this Pages container
  // (so the back button + list are in scope) and then asks us to select
  // the freshly-built child — this replaces the older flow of focusing
  // the report module directly with no Pages chrome.
  //
  // The dispatch can land BEFORE this component mounts (ChatBox attaches
  // the Pages pane in response to module.focus → React mounts us → the
  // listener registers — meanwhile the dispatch already fired in useChat).
  // To handle that case, useChat also writes the latest pending child to
  // sessionStorage; we check it on mount and consume if present.
  useEffect(() => {
    const handleOpenChild = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { parentPagesId?: string; childModuleId?: string; childSource?: 'module' | 'object' }
        | undefined;
      if (!detail || detail.parentPagesId !== module.id) return;
      if (typeof detail.childModuleId !== 'string' || !detail.childModuleId) return;
      const prefix = detail.childSource === 'object' ? 'o:' : 'm:';
      setCurrentPageId(`${prefix}${detail.childModuleId}`);
    };
    window.addEventListener('xavior:pages-open-child', handleOpenChild);

    // Mount-time check for a pending child request that fired before we
    // were listening. Uses sessionStorage so the value persists across the
    // render gap but is cleared on tab close.
    try {
      const raw = window.sessionStorage.getItem(`xavior:pages-pending-child:${module.id}`);
      if (raw) {
        window.sessionStorage.removeItem(`xavior:pages-pending-child:${module.id}`);
        const parsed = JSON.parse(raw) as { childModuleId?: string; childSource?: 'module' | 'object'; ts?: number };
        // Stale guard — ignore anything older than 10 minutes.
        const ageMs = parsed.ts ? Date.now() - parsed.ts : 0;
        if (parsed.childModuleId && ageMs < 10 * 60_000) {
          const prefix = parsed.childSource === 'object' ? 'o:' : 'm:';
          setCurrentPageId(`${prefix}${parsed.childModuleId}`);
        }
      }
    } catch {
      // sessionStorage unavailable (e.g. private mode) — ignore.
    }

    return () => window.removeEventListener('xavior:pages-open-child', handleOpenChild);
  }, [module.id]);

  // Fetch all workspace modules. staleTime: 0 ensures we always pick up
  // the latest config.html after a build finishes persisting — the prior
  // 30s cache window caused reopened pages to show blank content because
  // a pre-persist snapshot was still being served from cache.
  const { data: wsModules = [], isLoading } = useQuery({
    queryKey: queryKeys.modules.list(workspaceId),
    queryFn: () =>
      api.workspaces.listWorkspaceModules(token ?? null, workspaceId, {
        includeArchived: false,
      }),
    enabled: Boolean(token && workspaceId),
    staleTime: 0,
  });

  // Fetch archived modules (only when viewing archived)
  const { data: archivedModules = [] } = useQuery({
    queryKey: [...queryKeys.modules.list(workspaceId), 'archived'],
    queryFn: () => api.workspaces.listWorkspaceModules(token ?? null, workspaceId, { includeArchived: true }),
    enabled: Boolean(token && workspaceId && showArchived),
    staleTime: 10_000,
  });

  // Phase 4-6 Stage A: also read page objects (type=page) parented to this
  // Pages container. Runs in parallel with the legacy modules query and is
  // merged below into a unified PageEntry list. staleTime: 0 matches the
  // modules query so newly-persisted page objects show up immediately.
  const { data: pageObjectsResponse } = useQuery({
    queryKey: queryKeys.objects.list({ type: 'page', moduleId: module.id }),
    queryFn: () =>
      api.objects.getObjectsByType(token ?? null, 'page', { moduleId: module.id, limit: 200 }),
    enabled: Boolean(token && workspaceId),
    staleTime: 0,
  });
  const pageObjects: any[] = (pageObjectsResponse as any)?.items ?? [];

  const archivedPages = (archivedModules as any[])
    .filter((m: any) => (m.archived || m.archivedAt) && (m.type === 'report' || m.type === 'html_viewer' || m.type === 'pages'))
    .sort((a: any, b: any) => new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime());

  const handleRestorePage = useCallback(async (moduleId: string) => {
    if (!token) return;
    try {
      await api.workspaces.updateWorkspaceModule(token, workspaceId, moduleId, { archived: false } as any);
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
    } catch (err) { console.error('[Pages] Restore failed:', err); }
  }, [token, workspaceId, queryClient]);

  const handleDeletePage = useCallback(async (moduleId: string) => {
    if (!token) return;
    try {
      await api.workspaces.deleteWorkspaceModule(token, workspaceId, moduleId);
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
    } catch (err) { console.error('[Pages] Delete failed:', err); }
  }, [token, workspaceId, queryClient]);

  // --- Unified PageEntry merge (Phase 4-6 Stage A dual-read) ---
  //
  // Merges legacy module-sourced pages (html_viewer/report children of this
  // Pages container) with new page objects (type=page, module_id=this).
  //
  // Field-path note: `mapObject` on the API spreads `entity.attributes` to
  // the TOP LEVEL of the response (see commit f3baab3e), so `obj.html` and
  // `obj.legacyModuleId` are populated, NOT `obj.attributes.html` /
  // `obj.attributes.legacyModuleId`. We accept the nested form as a defensive
  // fallback in case the wire shape ever moves back to nested.
  //
  // Dedup: object-primary. Every page lives in the DB as BOTH a page object
  // and a legacy html_viewer/report module linked via `legacyModuleId`. The
  // OBJECT is the architectural target (it carries snapshots, the revert
  // tool, and the section-frame inspector). We hide any module whose paired
  // object is live in the list; modules survive only when their sibling
  // object is missing.
  //
  // Each entry keeps both the raw id and a prefixed composite id
  // ('m:<id>' / 'o:<id>') so selection/routing can route back to the right
  // data source.
  const pages = useMemo(() => {
    const entries: PageEntry[] = [];

    // Step 1: walk page objects (the new source of truth). Track which
    // legacyModuleIds they cover so we can hide the paired module row.
    const objectCoveredModuleIds = new Set<string>();
    for (const obj of pageObjects) {
      // Archived-page guard. The list API defaults to includeArchived=false
      // already, but pages can be marked archived in several ways depending
      // on the path they were archived through — check all of them.
      if ((obj as any).archived_at || (obj as any).archivedAt || (obj as any).status === 'archived') continue;

      // Reports own their content in the module's config.sections, not the
      // page object. The agent still writes a paired wrapper page object
      // (legacyType='report', html=''), which used to "cover" the report
      // module here and route the click through PageRenderer (reading the
      // empty html field) instead of ReportModule. Result: the user sees a
      // blank pane where their report should be. Skip the wrapper entirely
      // and let Step 2 below render the report module directly.
      const legacyType = readPageObjAttr<string>(obj, 'legacyType');
      if (legacyType === 'report') continue;

      const legacyId = readPageObjAttr<string>(obj, 'legacyModuleId');
      if (typeof legacyId === 'string' && legacyId.length > 0) {
        objectCoveredModuleIds.add(legacyId);
      }

      const objHtml = readPageObjAttr<string>(obj, 'html') || '';
      const objId = obj.id;
      entries.push({
        id: `o:${objId}`,
        source: 'object',
        rawId: objId,
        title: (obj as any).title || (obj as any).name || readPageObjAttr<string>(obj, 'title') || 'Untitled',
        html: objHtml,
        styleFamily: readPageObjAttr<string>(obj, 'styleFamily'),
        version: readPageObjAttr<number>(obj, 'version'),
        updatedAt: (obj as any).updated_at || (obj as any).updatedAt || (obj as any).created_at || (obj as any).createdAt || '',
        createdAt: (obj as any).created_at || (obj as any).createdAt || '',
        visibility: (obj as any).visibility || readPageObjAttr<string>(obj, 'visibility') || 'private',
        publish: readPageObjAttr<PageEntry['publish']>(obj, 'publish'),
        raw: obj,
      });
    }

    // Title-based fallback: if any object lacks a legacyModuleId (legacy
    // backfill rows or hand-created objects), still hide a module of the
    // same title so the user doesn't see a duplicate.
    const objectTitlesNormalised = new Set<string>(
      entries.map((e) => (e.title || '').trim().toLowerCase()).filter(Boolean),
    );

    // Step 2: walk modules. Skip any module whose id is covered by a page
    // object OR whose title matches a live object's title.
    for (const m of (wsModules as any[])) {
      if (m.id === module.id) continue;
      if (m.archived || m.archivedAt) continue;
      const isPageModule =
        m.parentId === module.id ||
        ((m.parentId == null || m.parentId === '') && (m.type === 'report' || m.type === 'html_viewer'));
      if (!isPageModule) continue;
      if (objectCoveredModuleIds.has(m.id)) continue;
      const titleNorm = ((m.label as string) || '').trim().toLowerCase();
      if (titleNorm && objectTitlesNormalised.has(titleNorm)) continue;

      const cfg = (m.config as any) || {};
      // Reports don't store rendered html — synthesise a fully-themed
      // thumbnail by rendering the same ReportSection components the live
      // preview uses, wrapped with the report's theme CSS + report.css.
      // Result: report thumbnails read as actual reports rather than
      // plain text on white.
      const thumbHtml: string =
        cfg.html
        || (m.type === 'report' ? synthesizeReportHtml(m.label || 'Report', cfg.sections, cfg.theme) : '');
      entries.push({
        id: `m:${m.id}`,
        source: 'module',
        rawId: m.id,
        title: m.label || 'Untitled',
        html: thumbHtml,
        styleFamily: cfg.styleFamily,
        version: cfg.version,
        updatedAt: m.updatedAt || m.createdAt || '',
        createdAt: m.createdAt || '',
        visibility: m.visibility || 'private',
        publish: cfg.publish,
        raw: m,
      });
    }

    entries.sort((a, b) => {
      const dA = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const dB = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return dB - dA;
    });

    return entries
      .filter((p) => {
        if (!searchQuery) return true;
        return (p.title || '').toLowerCase().includes(searchQuery.toLowerCase());
      })
      .filter((p) => {
        if (visibilityFilter === 'all') return true;
        // "Public" means the page has actually been published — not the page's
        // top-level `visibility` field (which is a separate "share scope"
        // concept that stays "private" by default and is rarely toggled).
        // The publish endpoint writes attrs.publish.{publicUrl, slug, ...},
        // so the presence of publicUrl is the authoritative published-ness
        // signal.
        const isPublished = Boolean(p.publish?.publicUrl);
        return visibilityFilter === 'public' ? isPublished : !isPublished;
      });
  }, [wsModules, pageObjects, module.id, searchQuery, visibilityFilter]);

  // Derive the live page object from the current pages array on every
  // render. This is the KEY fix for "blank iframe on reopen": a previous
  // implementation stored the full page object in state, which froze stale
  // `config.html` at click time and never updated even when React Query
  // refetched the modules list. Now the preview always reflects the latest
  // server state.
  const currentPage = useMemo(() => {
    if (!currentPageId) return null;
    // Composite id ('m:<id>' or 'o:<id>') — look in merged list first.
    // Family-tolerant: a selection set from a live-stream event can carry a
    // different FORM of the same page id (canonical `pg_*` vs raw module id,
    // `o:`/`m:` prefix vs the source that actually materialised). Resolve
    // via the id family so a form mismatch never strands the user on the
    // list mid-build. Exact composite matches still win inside the helper.
    const live = findEntryByPageIdFamily(pages, currentPageId);
    if (live) {
      return {
        id: live.id,
        source: live.source as 'module' | 'object',
        type: live.source === 'module' ? (live.raw.type as string) : 'page',
        label: live.title || 'Untitled',
        module: live.source === 'module' ? live.raw : undefined,
        object: live.source === 'object' ? live.raw : undefined,
      };
    }
    // Fallback: archived pages are still module-sourced only.
    const unprefixed = currentPageId.startsWith('m:') ? currentPageId.slice(2) : currentPageId;
    const arch = archivedPages.find((p: any) => p.id === unprefixed);
    if (!arch) return null;
    return {
      id: `m:${arch.id}`,
      source: 'module' as const,
      type: arch.type as string,
      label: (arch.label as string) || 'Untitled',
      module: arch,
      object: undefined,
    };
  }, [currentPageId, pages, archivedPages]);

  // Lightweight snapshot-count probe so the History icon is only rendered for
  // pages that actually have prior versions. Keyed by the SAME `page-snapshots`
  // tuple HistoryDrawer uses, so opening the drawer is free (React Query cache
  // hit). Only fires for object-sourced pages; module-sourced pages predate the
  // snapshot system and never have entries.
  const pageObjectIdForSnapshotsQuery =
    currentPage?.source === 'object'
      ? (currentPage.id.startsWith('o:') ? currentPage.id.slice(2) : currentPage.id)
      : null;
  const { data: currentPageSnapshots = [] } = useQuery({
    queryKey: ['page-snapshots', pageObjectIdForSnapshotsQuery ?? ''],
    queryFn: async () => {
      if (!pageObjectIdForSnapshotsQuery) return [] as any[];
      const res = await fetch(
        `${env.apiBaseUrl}/pages/${encodeURIComponent(pageObjectIdForSnapshotsQuery)}/snapshots`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            // Flat route — tenancy resolves from this header; without it a
            // JWT lacking a workspace claim 400s (WORKSPACE_CONTEXT_REQUIRED).
            ...(workspaceId ? { 'X-Workspace-Id': workspaceId } : {}),
          },
        },
      );
      if (!res.ok) return [] as any[];
      const body = await res.json().catch(() => ({}));
      return (body.items as any[]) || [];
    },
    enabled: !!pageObjectIdForSnapshotsQuery && !!token,
    staleTime: 60_000,
  });
  const hasPageHistory = currentPageSnapshots.length > 0;

  // Dispatch active-page-changed so ChatBox can include page context in agent requests.
  // IMPORTANT: `moduleId` must always be the actual html_viewer MODULE id (not the
  // pg_<xxx> object id). For object-sourced pages, the module id is in
  // attributes.legacyModuleId. For module-sourced pages, it's the rawId directly.
  // The edit flow uses this to PATCH the module via updateWorkspaceModule.
  useEffect(() => {
    const rawId = currentPage
      ? (currentPage.id.startsWith('m:') || currentPage.id.startsWith('o:')
          ? currentPage.id.slice(2)
          : currentPage.id)
      : null;
    // Resolve the underlying html_viewer module id for edit operations.
    // For object-sourced pages, the API mapper flattens attributes to the
    // top level — try `obj.legacyModuleId` first, fall back to the nested
    // form, finally to the raw object id.
    const obj: any = currentPage?.source === 'object' ? currentPage.object : null;
    const flatLegacy = typeof obj?.legacyModuleId === 'string' ? obj.legacyModuleId : undefined;
    const nestedLegacy = typeof obj?.attributes?.legacyModuleId === 'string' ? obj.attributes.legacyModuleId : undefined;
    const resolvedModuleId = currentPage
      ? (currentPage.source === 'object'
          ? (flatLegacy || nestedLegacy || rawId)
          : rawId)
      : null;
    window.dispatchEvent(new CustomEvent('xavior:active-page-changed', {
      detail: currentPage ? {
        pageId: currentPage.id,
        rawId: resolvedModuleId!,  // always the MODULE id, not the object id
        source: currentPage.source || 'module',
        title: currentPage.label || '',
        moduleType: currentPage.type,
      } : null,
    }));
    // Clear context when the Pages module unmounts
    return () => {
      window.dispatchEvent(new CustomEvent('xavior:active-page-changed', { detail: null }));
    };
  }, [currentPage]);

  // Auto-select the newest page when no page is currently selected and a
  // recently-created page exists (within the last 10 seconds). We DO open
  // empty html_viewer modules so HtmlViewerModule mounts early enough to
  // receive streaming `xavior:html-config-update` CustomEvents during the
  // build. HtmlViewerModule renders a "Building page..." skeleton for the
  // brief window before the first chunk arrives.
  //
  // A SET-but-not-yet-resolved id must bail here too: a pending selection
  // (open-child dispatch / the live-event fallback below) routinely points
  // at a row the queries haven't refetched yet. Auto-selecting `pages[0]`
  // over it would hijack the in-flight build/report with whatever page
  // happens to be newest — the html path would ping-pong back each chunk,
  // and the report path has no correcting event at all. Resolution is the
  // job of the family-tolerant `currentPage` lookup above once the row
  // lands, not this recovery.
  useEffect(() => {
    if (currentPageId) return;
    if (pages.length === 0) return;
    const newest = pages[0]; // already sorted by updatedAt DESC
    const createdAt = newest.createdAt || newest.updatedAt;
    if (!createdAt) return;
    const ageMs = Date.now() - new Date(createdAt).getTime();
    if (ageMs >= 10_000) return;
    setCurrentPageId(newest.id);
  }, [pages, currentPageId]);

  // Defense in depth: if a `xavior:html-config-update` event fires for an
  // html_viewer module that isn't currently opened, auto-open it. This
  // guarantees the user sees the real-time build even if the initial
  // auto-select missed the 10s freshness window for some reason.
  useEffect(() => {
    function onHtmlUpdate(e: Event) {
      const detail = (e as CustomEvent).detail as { moduleId: string };
      if (!detail?.moduleId) return;
      // The streaming event keys by the canonical `pg_*` page-object id (the
      // dispatch path canonicalises via toCanonicalPageId) while entries can
      // be object-sourced (`rawId: pg_*`) or module-sourced (`rawId: <raw>`).
      // Match the whole id FAMILY — a strict equality here dropped every
      // mid-build event for module-sourced rows (pg_* vs raw mismatch) and
      // re-fabricated a broken selection on each chunk.
      const target = findEntryByPageIdFamily(pages, detail.moduleId);
      if (!target) {
        // toCanonicalPageId (not an inline startsWith check) — it strips the
        // historically-leaked `o:`/`m:` composite prefixes first. An inline
        // `pg_` test on a composite form would garble the id (`pg_o:pg_X`),
        // mis-keying the once-per-build gate below AND producing a selection
        // that no entry could ever resolve.
        const canonicalId = toCanonicalPageId(detail.moduleId) ?? detail.moduleId;
        // The page isn't in the merged list yet (created moments ago; the
        // row queries haven't refetched). Refetch BOTH row sources — the API
        // mirrors every build into a page OBJECT, so invalidating only the
        // modules query never surfaces the entry that will actually render.
        //
        // ONCE per build id: these events arrive per streamed chunk, and
        // invalidateQueries' default cancelRefetch would cancel + restart
        // the in-flight refetch on every chunk — fast streams could starve
        // the very refetch that lands the row. The first invalidation is
        // the one that matters; html.complete and the row onClick remain
        // as backstops.
        if (!invalidatedBuildIdsRef.current.has(canonicalId)) {
          invalidatedBuildIdsRef.current.add(canonicalId);
          queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
          queryClient.invalidateQueries({
            queryKey: queryKeys.objects.list({ type: 'page', moduleId: module.id }),
          });
        }
        // Select the entry the refetch WILL produce: the object mirror
        // (`o:pg_*`). The previous fabricated `m:pg_*` form resolved to
        // nothing — parking the user on the list view with the auto-select
        // recovery disabled until they clicked a row by hand.
        setCurrentPageId(`o:${canonicalId}`);
        return;
      }
      if (currentPageId === target.id) return;
      setCurrentPageId(target.id);
    }
    window.addEventListener('xavior:html-config-update', onHtmlUpdate as EventListener);
    return () => window.removeEventListener('xavior:html-config-update', onHtmlUpdate as EventListener);
  }, [pages, currentPageId, queryClient, workspaceId, module.id]);

  const handleRename = useCallback(
    async (entryId: string, newLabel: string) => {
      if (!token || !newLabel.trim()) return;
      const { source, rawId } = parseEntryId(entryId);
      try {
        if (source === 'object') {
          await api.objects.updateObject(token, 'page', rawId, {
            title: newLabel.trim(),
          } as any);
          queryClient.invalidateQueries({
            queryKey: queryKeys.objects.list({ type: 'page', moduleId: module.id }),
          });
        } else {
          await api.workspaces.updateWorkspaceModule(
            token,
            workspaceId,
            rawId,
            { label: newLabel.trim() } as any,
          );
          queryClient.invalidateQueries({
            queryKey: queryKeys.modules.list(workspaceId),
          });
        }
      } catch (err) {
        console.error("[Pages] Rename failed:", err);
      }
      setRenamingId(null);
    },
    [token, workspaceId, queryClient, module.id],
  );

  const handleArchive = useCallback(
    async (entryId: string) => {
      if (currentPageId === entryId) setCurrentPageId(null);
      if (!token) return;
      const { source, rawId } = parseEntryId(entryId);

      // Snapshot the workspace id ONCE at click time so a container swap
      // mid-archive can't have us cancel against workspace A and archive
      // in workspace B. All subsequent calls in this handler use this
      // snapshot — including cancelBuild's X-Workspace-Id header.
      const archiveWorkspaceId = workspaceId;

      // Cancel any in-flight x-agent build for this page BEFORE we DELETE.
      // Without this, the build's persistence loop would re-upsert the
      // page object after the DELETE succeeds, resurrecting the row
      // milliseconds later (issue #1817). Best-effort with a short
      // timeout — if the call fails, the API's archived-module guard
      // keeps UX correct.
      //
      // Build registry keys by html_viewer/report module id, NEVER by
      // page-object id. For object-sourced rows, the build runs against
      // the paired legacyModuleId; for module-sourced rows, the build
      // runs against rawId. Sending an extra cancel POST for the
      // page-object id would always 404 — wasted round-trip.
      let moduleIdToCancel: string | undefined;
      if (source === 'object') {
        const obj = pageObjects.find((o: any) => o.id === rawId);
        moduleIdToCancel = readPageObjAttr<string>(obj, 'legacyModuleId');
      } else {
        moduleIdToCancel = rawId;
      }
      if (moduleIdToCancel) {
        await cancelBuild(moduleIdToCancel, {
          authToken: token,
          timeoutMs: 500,
          workspaceId: archiveWorkspaceId,
        });
      }

      try {
        if (source === 'object') {
          // Page objects are usually paired with a legacy html_viewer module
          // via legacyModuleId. If we only delete the object, the sibling
          // module pops back into the list — looks to the user like archive
          // did nothing. Archive both in one go.
          //
          // Wrap each request individually so a failure on one doesn't
          // skip the other — if deleteObject fails, we still try to
          // archive the legacy module (and vice-versa). Both are
          // best-effort; the optimistic-hide already gave the user
          // their visual confirmation.
          const obj = pageObjects.find((o: any) => o.id === rawId);
          const legacyModuleId = readPageObjAttr<string>(obj, 'legacyModuleId');
          try {
            await api.objects.deleteObject(token, 'page', rawId, true);
          } catch (delErr) {
            console.warn(
              '[Pages] Failed to delete page object — will still attempt sibling archive:',
              delErr,
            );
          }
          if (legacyModuleId) {
            try {
              await api.workspaces.updateWorkspaceModule(
                token,
                archiveWorkspaceId,
                legacyModuleId,
                { archived: true } as any,
              );
            } catch (innerErr) {
              // Sibling archive is best-effort — the object is the user-visible
              // entry and it's already gone. Log and move on.
              console.warn(
                '[Pages] Archived object but failed to archive sibling legacy module:',
                innerErr,
              );
            }
          }
          queryClient.invalidateQueries({
            queryKey: queryKeys.objects.list({ type: 'page', moduleId: module.id }),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.modules.list(archiveWorkspaceId),
          });
        } else {
          // Archiving a module. Find the paired page object (if any) and
          // hard-delete it so the list doesn't show an orphan object-sourced
          // row after the module is gone.
          const pairedObject = pageObjects.find(
            (o: any) => readPageObjAttr<string>(o, 'legacyModuleId') === rawId,
          ) as { id?: string } | undefined;
          await api.workspaces.updateWorkspaceModule(
            token,
            workspaceId,
            rawId,
            { archived: true } as any,
          );
          if (pairedObject?.id) {
            try {
              await api.objects.deleteObject(token, 'page', pairedObject.id, true);
            } catch (innerErr) {
              console.warn(
                '[Pages] Archived module but failed to delete paired object:',
                innerErr,
              );
            }
          }
          queryClient.invalidateQueries({
            queryKey: queryKeys.modules.list(workspaceId),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.objects.list({ type: 'page', moduleId: module.id }),
          });
        }
      } catch (err) {
        // Best-effort — log for visibility but don't surface a popup. The
        // server-side cancel + cascade is the proper fix (PR #1853); if the
        // request fails, the next refetch will reflect reality.
        console.warn('[Pages] Archive request failed:', err);
      }
    },
    [token, workspaceId, queryClient, currentPageId, module.id, pageObjects],
  );

  const handleCreatePage = useCallback(() => {
    if (!newPageTitle.trim()) return;
    // Phrasing matters — the agent's intent classifier picks up the verb
    // and noun. "Create a report about" routes through the report builder;
    // "Build me a webpage about" hits the webpage skill (matches the
    // webpageKeywords regex in apps/x-agent/src/routes/chat.ts:1992).
    const message = newPageKind === 'webpage'
      ? `Build me a webpage about: ${newPageTitle.trim()}`
      : `Create a report about: ${newPageTitle.trim()}`;
    window.dispatchEvent(new CustomEvent('xavior:send-chat-message', { detail: { message } }));
    setShowNewPageModal(false);
    setNewPageTitle('');
  }, [newPageTitle, newPageKind]);

  // ---- Preview View ----
  if (currentPage) {
    return (
      <div
        className="module"
        style={{
          display: "flex",
          flexDirection: "column",
          // flex: 1 instead of height: 100% — percentage heights don't
          // resolve reliably through the nested flex chain above
          // (.chat-module-pane-content → .module-renderer-wrapper → here),
          // which caused this wrapper to collapse to 82px (header content
          // size only). flex: 1 fills the available space from the parent's
          // flex layout directly, no percentage cascade needed.
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            borderBottom:
              "1px solid var(--color-border-light, rgba(0,0,0,0.06))",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          <button
            onClick={() => setCurrentPageId(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              color: "var(--color-text-muted)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <span
            style={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              color: "var(--color-text)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {currentPage.label}
          </span>
          {/* History icon — only shown when the page actually has prior
              versions (snapshot count > 0). Scoped to object-sourced pages;
              legacy module-sourced pages predate the snapshot system.
              Rendered BEFORE the Edit button so Edit ends up as the
              rightmost control and lines up vertically with the outer
              module chrome's Close X (same is-icon-only sizing). */}
          {currentPage.source === 'object' && hasPageHistory && (
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              aria-label="View page history"
              title="History"
              className="module-toolbar-button is-icon-only"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </button>
          )}
          {/* Edit and Download were here previously; they've been
              consolidated into PageRenderer's share-bar (along with
              Copy / Share / Unpublish / Publish / Preview) so all
              per-page actions live in one place. The header keeps
              History (page-version-specific) plus the Back button +
              title. */}
        </div>
        {/* Version history drawer — mounted at the same layout level so it
            is dismissed when the user leaves the preview view (the preview
            branch is re-rendered as List when currentPageId clears). */}
        {currentPage.source === 'object' && (
          <HistoryDrawer
            open={historyOpen}
            onClose={() => setHistoryOpen(false)}
            pageObjectId={
              currentPage.id.startsWith('o:')
                ? currentPage.id.slice(2)
                : currentPage.id
            }
            token={token ?? null}
            onRestored={() => {
              // Revert triggers a PATCH on the page object; invalidate both
              // lookup paths so the preview reflects the restored html. The
              // iframe-patch path from commit 7 handles the in-place update.
              queryClient.invalidateQueries({
                queryKey: queryKeys.objects.list({ type: 'page', moduleId: module.id }),
              });
              queryClient.invalidateQueries({
                queryKey: queryKeys.modules.list(workspaceId),
              });
            }}
          />
        )}
        {/* Content — must be a flex column with min-height:0 so the inner
            .module element can resolve `height: 100%` against a real height.
            Without minHeight:0 + display:flex, an `overflow:auto` flex item
            collapses to its content height, which during HTML streaming is
            the iframe's intrinsic 150px default → broken short scrollable
            iframe. */}
        <div
          className="xv-scroll"
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            // Reports have their own internal scroll; html_viewer iframes
            // scroll their content internally via the iframe element itself.
            // Applying `overflow: auto` here for html_viewer creates a
            // second scroll container that competes with the iframe's
            // absolute-positioned fill and can cause the iframe to render
            // at a collapsed height.
            overflow: currentPage.type === "report" ? "auto" : "hidden",
          }}
        >
          {currentPage.source === 'object' ? (
            <PageRenderer object={currentPage.object} conversationId={conversationId} />
          ) : currentPage.type === 'report' ? (
            <ReportModule module={currentPage.module} />
          ) : (
            <HtmlViewerModule
              module={currentPage.module}
              conversationId={conversationId}
            />
          )}
        </div>
      </div>
    );
  }

  // ---- List View ----
  return (
    <div className="module" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Visibility filter */}
      <div className="module-nav">
        <div className="module-nav__primary">
          <div className="module-toolbar-button-group">
            <button type="button" className={`module-toolbar-button ${visibilityFilter === 'all' ? 'is-active' : ''}`}
              onClick={() => setVisibilityFilter('all')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              <span className="module-toolbar-label" data-text="All">All</span>
            </button>
            <button type="button" className={`module-toolbar-button ${visibilityFilter === 'private' ? 'is-active' : ''}`}
              onClick={() => setVisibilityFilter('private')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <span className="module-toolbar-label" data-text="Private">Private</span>
            </button>
            <button type="button" className={`module-toolbar-button ${visibilityFilter === 'public' ? 'is-active' : ''}`}
              onClick={() => setVisibilityFilter('public')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              <span className="module-toolbar-label" data-text="Public">Public</span>
            </button>
          </div>
        </div>
        <div className="module-nav__secondary">
          <MobileSearchToggle targetId="pages-search-toolbar" />
          <MobileActionButton onClick={() => { setShowNewPageModal(true); setNewPageTitle(''); setNewPageKind('report'); }} title="New report" />
        </div>
      </div>

      {/* Search bar — matches Drive's wrapper exactly (no module-search-row
          class, no inline button — the "+ New" pill lives in the column
          header row below). */}
      <div id="pages-search-toolbar">
        <ModuleSearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          onClose={() => setSearchQuery('')}
          placeholder="Search reports..."
          resultCount={searchQuery ? pages.length : undefined}
          autoFocus={false}
        />
      </div>

      {/* New Page modal — type picker + topic input */}
      {showNewPageModal && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '15vh', zIndex: 10000 }}
          onClick={() => { setShowNewPageModal(false); setNewPageTitle(''); }}
        >
          <div
            style={{ background: 'var(--color-surface)', borderRadius: 12, padding: 24, width: 520, maxWidth: '92vw', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, color: 'var(--color-text)' }}>New Report</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-faint)', marginBottom: 14, lineHeight: 1.5 }}>
              Select either a standard report or HTML page.
            </div>

            {/* Type picker — two cards, one selected at a time. */}
            <div
              role="radiogroup"
              aria-label="Page type"
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}
            >
              {([
                {
                  kind: 'report' as const,
                  title: 'Standard report',
                  blurb: 'Structured findings with sections, charts, and stats. Researched from the web or your workspace data.',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="8" y1="13" x2="16" y2="13" />
                      <line x1="8" y1="17" x2="13" y2="17" />
                    </svg>
                  ),
                },
                {
                  kind: 'webpage' as const,
                  title: 'HTML page',
                  blurb: 'A bespoke design — landing page, portfolio, fan site. Mimic any brand or invent its own look.',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="4" width="18" height="16" rx="2" />
                      <line x1="3" y1="9" x2="21" y2="9" />
                      <circle cx="6.5" cy="6.5" r="0.5" fill="currentColor" />
                      <circle cx="9" cy="6.5" r="0.5" fill="currentColor" />
                    </svg>
                  ),
                },
              ]).map((opt) => {
                const selected = newPageKind === opt.kind;
                return (
                  <button
                    key={opt.kind}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setNewPageKind(opt.kind)}
                    style={{
                      textAlign: 'left',
                      padding: '12px 14px',
                      borderRadius: 8,
                      border: `1.5px solid ${selected ? 'var(--color-brand)' : '#e5e7eb'}`,
                      background: selected ? 'var(--xavior-blue-bg)' : '#fff',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, color: selected ? 'var(--color-brand-hover)' : '#1e293b' }}>
                      {opt.icon}
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{opt.title}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-faint)', lineHeight: 1.45 }}>{opt.blurb}</div>
                  </button>
                );
              })}
            </div>

            <input
              autoFocus
              value={newPageTitle}
              onChange={(e) => setNewPageTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newPageTitle.trim()) {
                  handleCreatePage();
                }
                if (e.key === 'Escape') { setShowNewPageModal(false); setNewPageTitle(''); }
              }}
              placeholder={
                newPageKind === 'webpage'
                  ? 'e.g. a Stripe-style landing page for Acme'
                  : 'e.g. the history of artificial intelligence'
              }
              style={{
                width: '100%', height: 44, padding: '0 14px', border: '1px solid #e5e7eb',
                borderRadius: 8, fontSize: 14, background: 'var(--color-surface)', color: '#1f2937',
                outline: 'none', boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--color-brand)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = '#e5e7eb')}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button
                onClick={() => { setShowNewPageModal(false); setNewPageTitle(''); }}
                style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e5e7eb', background: 'var(--color-surface)', fontSize: 13, cursor: 'pointer', color: 'var(--color-text-muted)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePage}
                disabled={!newPageTitle.trim()}
                style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--color-brand)', color: '#fff', fontSize: 13, cursor: 'pointer', opacity: newPageTitle.trim() ? 1 : 0.5 }}
              >
                {newPageKind === 'webpage' ? 'Build webpage' : 'Create report'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Archived pages view */}
      {showArchived && (
        <div className="module-content" style={{ padding: '0 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0 8px' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)' }}>
              Archived Pages ({archivedPages.length})
            </span>
            <button
              onClick={() => setShowArchived(false)}
              style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, padding: '4px 12px', fontSize: 12, color: 'var(--color-text-subtle)', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-surface-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              ← Back
            </button>
          </div>
          {archivedPages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-text-faint)', fontSize: 13 }}>No archived pages</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {archivedPages.map((page: any) => (
                <div key={page.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', marginBottom: 4 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {page.label || 'Untitled'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 2 }}>
                      {page.type === 'report' ? 'Report' : 'Page'}
                      {page.updatedAt ? ` · ${new Date(page.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRestorePage(page.id)}
                    style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, padding: '4px 10px', fontSize: 12, color: 'var(--color-primary)', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(var(--color-primary-rgb), 0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                  >
                    Unarchive
                  </button>
                  <button
                    onClick={() => { if (window.confirm(`Permanently delete "${page.label || 'Untitled'}"? This cannot be undone.`)) handleDeletePage(page.id); }}
                    style={{ background: 'none', border: 'none', borderRadius: 6, padding: '4px 6px', color: 'var(--color-text-faint)', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-faint)'; }}
                    title="Delete permanently"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Page list */}
      {!showArchived && !isLoading && pages.length === 0 && searchQuery && (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--color-text-tertiary)' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 4 }}>
            No pages matching &ldquo;{searchQuery}&rdquo;
          </div>
        </div>
      )}
      {!showArchived && !isLoading && pages.length === 0 && !searchQuery && (
        <ModuleEmptyState
          icon={<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>}
          iconColor="#3b82f6"
          title="AI-generated reports and pages"
          description={<>Ask Xavior to create reports, web pages, and published content.<br />From research summaries to business reports — generated in seconds.</>}
          buttonLabel="Create a report or page"
          onButtonClick={() => { setShowNewPageModal(true); setNewPageTitle(''); setNewPageKind('report'); }}
        />
      )}
      {!showArchived && pages.length > 0 && (
        <div style={{ position: 'relative' }}>
          <div className="pages-list-header" role="row">
            <span role="columnheader" style={{ paddingLeft: 8 }}>Thumbnail</span>
            <span role="columnheader" style={{ paddingLeft: 16 }}>Name</span>
            <span role="columnheader" className="pages-list-header__date">Date</span>
            <span aria-hidden="true" />
          </div>
          {/* "+ New" pill — same geometry, color, and hover behaviour as
              DriveModule's header New button (anchored to the right edge of
              the header row). */}
          <span style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-65%)', display: 'flex', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => { setShowNewPageModal(true); setNewPageTitle(''); setNewPageKind('report'); }}
              title="New page"
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-brand-hover)',
                borderRadius: 22,
                boxSizing: 'border-box',
                cursor: 'pointer',
                height: 32,
                padding: '0 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                color: 'var(--color-brand-hover)',
                fontSize: 13,
                fontWeight: 500,
                flexShrink: 0,
                transition: 'background 0.15s',
                fontFamily: "'DM Sans', sans-serif",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--xavior-blue-bg)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New
            </button>
          </span>
        </div>
      )}
      {!showArchived && (
        <div className="pages-list-compact xv-scroll">
          {pages.map((page: any) => (
            <div
              key={page.id}
              className="pages-item"
              onClick={() => {
                if (renamingId === page.id) return;
                // Force a fresh fetch before opening so we don't show a stale
                // cached html blob (can happen if an in-flight build's persist
                // landed after the query's initial load). Invalidate BOTH sources
                // since the entry could be either module or object backed.
                queryClient.invalidateQueries({
                  queryKey: queryKeys.modules.list(workspaceId),
                });
                queryClient.invalidateQueries({
                  queryKey: queryKeys.objects.list({ type: 'page', moduleId: module.id }),
                });
                setCurrentPageId(page.id);
              }}
            >
              <PageRowThumbnail
                pageId={page.id}
                title={page.title || 'Untitled'}
                html={page.html}
              />

              {renamingId === page.id ? (
                <input
                  autoFocus
                  className="pages-item-title-input"
                  value={renameText}
                  onChange={(e) => setRenameText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRename(page.id, renameText);
                    if (e.key === 'Escape') {
                      setRenamingId(null);
                      e.currentTarget.dataset.cancelled = 'true';
                    }
                  }}
                  onBlur={(e) => {
                    if (e.currentTarget.dataset.cancelled !== 'true') {
                      handleRename(page.id, renameText);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="pages-item-title">
                  {page.title || 'Untitled'}
                </span>
              )}

              <span className="pages-item-meta">
                {formatDate(page.updatedAt || page.createdAt)}
              </span>

              <button
                className="pages-item-menu"
                onClick={(e) => {
                  e.stopPropagation();
                  setContextMenu(
                    contextMenu?.id === page.id
                      ? null
                      : {
                          id: page.id,
                          name: page.title || 'Untitled',
                          x: e.clientX,
                          y: e.clientY,
                        },
                  );
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Context menu */}
      {contextMenu &&
        createPortal(
          <div
            style={{ position: "fixed", inset: 0, zIndex: 10000 }}
            onClick={() => setContextMenu(null)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                top: contextMenu.y,
                left: contextMenu.x,
                transform: "translate(-100%, 0)",
                background: "var(--color-surface)",
                borderRadius: 8,
                boxShadow: "var(--popover-shadow, 0 4px 16px rgba(0,0,0,0.15))",
                padding: "4px 0",
                minWidth: 140,
                fontSize: 13,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <button
                onClick={() => {
                  setRenamingId(contextMenu.id);
                  setRenameText(contextMenu.name);
                  setContextMenu(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "8px 14px",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: 13,
                  color: "var(--color-text)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "var(--color-surface-raised, #f5f5f5)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "none";
                }}
              >
                Rename
              </button>
              <button
                onClick={() => {
                  handleArchive(contextMenu.id);
                  setContextMenu(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "8px 14px",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: 13,
                  color: "var(--color-danger, #dc2626)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "var(--color-surface-raised, #f5f5f5)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "none";
                }}
              >
                Archive
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
